import { createFileRoute, Navigate } from "@tanstack/react-router";

import { isCustomerParticipantRole } from "../../lib/access";
import { useProject, useProjectId } from "../../shell/project";
import { leadSearch } from "../../workspaces/agents/agent-location";
import { agentWorkspaceEnabled } from "../../workspaces/agents/availability";
import { usePageArtifactAddress } from "../../workspaces/agents/beside/address";
import { LeadConversation } from "../../workspaces/agents/LeadConversation";

export const Route = createFileRoute("/_console/p/$org/$project/")({
  // Start the addressed conversation on click. Optional operations load their own data
  // when opened beside it.
  loaderDeps: ({ search }) => ({ chat: search.chat }),
  loader: ({ context, params, cause, deps }) => {
    if (cause === "stay") return;
    const address = { org: params.org, project: params.project };
    if (agentWorkspaceEnabled) {
      const ready = import("../../workspaces/agents/prefetch").then(({ prefetchAgentPage }) =>
        prefetchAgentPage(context.queryClient, address, undefined, deps.chat),
      );
      if (import.meta.env.SSR) return ready;
      void ready;
    }
  },
  validateSearch: leadSearch,
  component: ProjectHomeRoute,
});

/** A project opens on its operation: the lead agent's conversation, with the month it runs
 *  beside it. A customer reviewer opens on the results the project holds. */
function ProjectHomeRoute() {
  const params = Route.useParams();
  const { selected } = useProject();
  if (isCustomerParticipantRole(selected?.managedAccessRole))
    return <Navigate to="/p/$org/$project/library" params={params} replace />;
  return <ProjectHome />;
}

function ProjectHome() {
  const projectId = useProjectId();
  const { address } = useProject();
  const { chat, tab, revision, artifact, pane, pinned } = Route.useSearch();
  const navigate = Route.useNavigate();
  const shown = usePageArtifactAddress({
    artifact,
    pane,
    pinned,
    onAddress: (next, replace) => void navigate({ search: (previous) => ({ ...previous, ...next }), replace }),
  });
  if (!address) return null;
  return (
    <LeadConversation
      projectId={projectId}
      address={address}
      chat={chat}
      tab={tab}
      revision={revision}
      artifact={shown}
      onSelectSession={(sessionId, options) =>
        void navigate({
          search: (previous) => ({
            ...previous,
            chat: sessionId,
            ...(options?.replace ? {} : { tab: undefined, revision: undefined, artifact: undefined, pane: undefined }),
          }),
          replace: options?.replace,
        })
      }
      onTabChange={(next) => void navigate({ search: (previous) => ({ ...previous, tab: next, revision: undefined }) })}
    />
  );
}
